import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchB2cDisabled,
  fetchSubscriptionStatusThunk,
  selectDownloadZip,
  selectExpired,
  selectID,
  selectiDB,
  selectInstitutionLoading,
  selectVersion,
  selectZip,
  setExpireSubscription,
  setInstitutionLoading,
  updateDownloadIDBProgress,
  updateDownloadProgress,
  updateDownloadZipProgress,
  updateInstitution,
  updateState,
  updateUser,
} from "../features/counter/counterSlice.js";
import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { tgoa } from "../features/search/util.js";
import { logoutCleanUp, userLogged } from "../utils/index.js";
import { isSalesforceEnabled } from "../utils/featureFlagSalesforceUtils.js";
import { useAccount, useMsal } from "@azure/msal-react";
import flags from "../features/featureFlags/flags.js";
import JSZip from "jszip";
import { IDB_KEY_LENGTH } from "../constants/ccms";
import { isAutomateTest } from "../features/featureFlags/automationChecker";
import useWhitelabelAvailability from "./useWhitelabelAvailability";
import { apiService } from "../api/apiAdapter";
import {
  APP_ID_GROUPS,
  authDetailsFromClaims,
  checkSubscription,
  fetchInstitutionFromIP,
  getEmail,
  getInstitutionFromAccessKey,
  getReferrer,
  isSubscriptionActiveNow,
  validateSubscription,
  validateSubscriptionForAccess,
} from "../utils/authUtils.js";
import flagContext from "../features/featureFlags/flagContext.js";

export const TYPE_USER = "user";
export const TYPE_INSTITUTION = "institution";
const CACHE_SUBSCRIBED_KEY = "subscribedAppsAccess";

// ============================================================================
// MODULE-LEVEL WORKER START CONTROL
// ============================================================================
// Prevents duplicate worker starts across ALL component instances and renders
// - Set synchronously BEFORE any async operations (prevents race conditions)
// - Persists across component remounts on same page
// - Resets on page refresh (module reload) to allow IDB resume
// - Reset when IDB complete (allows restart on version change)
let workersStartedGlobal = false;
const EMPTY_SUBSCRIPTIONS = [];

// control the deduplication for getReqInfo API call
let getReqInfoPromise = null;
let getReqInfoCache = null;
let getReqInfoCalled = false;
let msalUserLoginEventSent = false;

const parsedInstituteForUser = (authID) => {
  let parsedInstition;
  if (authID.u.institution !== undefined) {
    parsedInstition = authID.u.institution;
  } else {
    const email = getEmail(authID.u);
    const tgoaEmail = "member@" + email?.split("@")[1];
    parsedInstition = tgoa[tgoaEmail];
  }
  return parsedInstition || authID.u.i || null;
};

const authDetails = (authId) => {
  if (!authId) return null;

  const { i, u } = authId;

  if (i && i !== " ") {
    return {
      type: TYPE_INSTITUTION,
      institution: i.name,
      institutionName: i.name,
    };
  }

  if (u && u !== " ") {
    const parsedInstitute = parsedInstituteForUser(authId);
    return {
      type: TYPE_USER,
      email: getEmail(u),
      institution: u.i || parsedInstitute || null,
      institutionName: parsedInstitute,
      givenName: u.name?.split(" ")[0] || null, // name is displayName in ADB2C it is required field
      familyName: u.name?.split(" ").slice(1).join(" ") || null,
    };
  }

  return null;
};

/**
 * Check cache status for all three zip files
 * @param {string} version - CCMS version
 * @returns {Promise<{allCached: boolean, cachedFiles: string[], uncachedFiles: string[]}>}
 */
const checkZipCacheStatus = async (version) => {
  try {
    const cache = await caches.open(import.meta.env.REACT_APP_CACHE_NAME);
    const types = [
      { type: "", name: "content" },
      { type: "_images", name: "images" },
      { type: "_PDFs", name: "PDFs" },
    ];

    const cacheStatus = await Promise.all(
      types.map(async ({ type, name }) => {
        const url = `/viewTopic/${version}${type}.zip?__WB_REVISION__=${import.meta.env.REACT_APP_CONTENT_VERSION}`;
        const cached = await cache.match(url);
        return { name, cached: !!cached };
      }),
    );

    const allCached = cacheStatus.every((status) => status.cached);
    const cachedFiles = cacheStatus.filter((s) => s.cached).map((s) => s.name);
    const uncachedFiles = cacheStatus.filter((s) => !s.cached).map((s) => s.name);

    return { allCached, cachedFiles, uncachedFiles };
  } catch (err) {
    console.warn("Could not check cache status:", err);
    return {
      allCached: false,
      cachedFiles: [],
      uncachedFiles: ["content", "images", "PDFs"],
    };
  }
};

/**
 * @typedef {Object} InstitutionUser
 * @property {string} name - Name of the institution user
 *
 * @typedef {Object} signInNames
 * @property {string} emailAddress - Email address used for sign-in
 *
 *
 * @typedef {Object} RegularUser
 * @property {string} email - Email address of the user
 * @property {string} given_name - Given name of the user
 * @property {string} i - Institution name associated with the user
 * @property {string} [signInNames] - Email address used for sign-in (optional)
 * @property {string} [family_name] - Family name of the user (optional)
 *
 * @typedef {Object} AuthID
 * @property {InstitutionUser} [i] - Institution user (optional)
 * @property {RegularUser} [u] - Regular user (optional)
 * @property {number} [exp] - Expiration timestamp (optional)

 * Example structure of `authID`:
 * {
 *   i?: { // Institution user (optional)
 *     name: string
 *   },
 *   u?: { // Regular user (optional)
 *     email: string,
 *     i: string,
 *     given_name: string,
 *     family_name?: string,
 *     signInNames {
 *         emailAddress: string // Email address used for sign-in
 *     },
 *     exp?: number // Expiration timestamp (optional)
 *   },
 * }
 */
const AuthContext = createContext(null);

const useAuthValue = () => {
  const dispatch = useDispatch();
  const currentApp = import.meta.env.VITE_BRAND || "tg";
  const authID = useSelector(selectID);
  const institutionLoading = useSelector(selectInstitutionLoading);
  const b2cDisabled = useBooleanFlagValue(flags.B2C_DISABLE_AUTH, false);
  const { filterByAvailability } = useWhitelabelAvailability();
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);
  const [silentAuthAccount, setSilentAuthAccount] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [msalLoading, setMsalLoading] = useState(true);
  const [institutionAuth, setInstitutionAuth] = useState(null);
  const loginVersion = useSelector((state) => state.institutionLogin?.version);
  // Institution checking state
  const zipFile = useSelector(selectZip); // Merged zip for app use
  const downloadZip = useSelector(selectDownloadZip); // Temporary parts during merge
  const ccmsversion = useSelector(selectVersion);
  const expired = useSelector(selectExpired);
  const iDB = useSelector(selectiDB);
  const [institution, setInstitution] = useState(" ");
  const [silentLoginAttempted, setSilentLoginAttempted] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionCheckAttempted, setSubscriptionCheckAttempted] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [institutionAuthChecked, setInstitutionAuthChecked] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(false);
  const [userProfileError, setUserProfileError] = useState(null);
  const institutionAuthRef = useRef(null);
  const lastB2cCheckKeyRef = useRef(null);
  const mergeSubscriptionDetails = useCallback((details, institution) => {
    const institutionSubscriptions = institution?.subscriptions || [];
    const b2cSubscriptions = details?.b2cSubscriptions || details?.allSubscriptions || [];
    return {
      ...details,
      institutionSubscriptions,
      b2cSubscriptions,
      allSubscriptions: [...institutionSubscriptions, ...b2cSubscriptions],
    };
  }, []);
  const institutionSubscriptions = useMemo(
    () => institutionAuth?.subscriptions || EMPTY_SUBSCRIPTIONS,
    [institutionAuth],
  );
  const resolvedTargetApp = sessionStorage.getItem("targetApp") || currentApp;
  const buildAuthDetails = useCallback((claims, authId, institution, subscription) => {
    if (claims) return authDetailsFromClaims(claims);
    if (authId?.u && authId?.u !== " ") return authDetails(authId);

    if (institution || subscription) {
      const name =
        institution?.institutionName ||
        institution?.institution ||
        subscription?.institutionName ||
        subscription?.institution ||
        null;
      return {
        type: TYPE_INSTITUTION,
        institution: name,
        institutionName: name,
      };
    }
    return authDetails(authId);
  }, []);
  // Lazy worker initialization
  const downloadWorkerRef = useRef(null);
  const getDownloadWorker = () => {
    if (!downloadWorkerRef.current && typeof Worker !== "undefined") {
      downloadWorkerRef.current = new Worker(new URL("../workers/download.js", import.meta.url), {
        type: "module",
      });
    }
    return downloadWorkerRef.current;
  };

  // institution auth checks
  useEffect(() => {
    let cancelled = false;

    const initInstitutionAuth = async () => {
      // If the user explicitly logged out this session, skip IP check to
      // prevent immediate auto-login. Flag is sessionStorage so it clears
      // automatically when the tab/window is closed.
      if (sessionStorage.getItem("explicitLogout")) {
        console.log("useAuth: explicitLogout set, skipping institution auth");
        setInstitutionAuthChecked(true);
        return;
      }

      // Try cached token first
      let institution = getInstitutionFromAccessKey();

      // If no valid cached token, try IP-based check
      if (!institution) {
        try {
          institution = await fetchInstitutionFromIP(getReferrer());
        } catch (err) {
          console.warn("useAuth - institution IP lookup failed:", err);
          institution = null;
        }
      }

      if (!cancelled) {
        setInstitutionAuth(institution);
        setInstitutionAuthChecked(true);
      }
    };

    initInstitutionAuth();

    return () => {
      cancelled = true;
    };
  }, [loginVersion]);
  useEffect(() => {
    institutionAuthRef.current = institutionAuth;
  }, [institutionAuth]);

  const resolvedAccount = account || silentAuthAccount;

  // Get email from claims directly
  const userEmail = useMemo(() => {
    if (resolvedAccount?.idTokenClaims?.email) return resolvedAccount.idTokenClaims.email;
    if (resolvedAccount?.username) return resolvedAccount.username;
    if (resolvedAccount?.idTokenClaims?.["signInNames.emailAddress"])
      return resolvedAccount.idTokenClaims["signInNames.emailAddress"];

    // Fall back to getting email from the parsed authDetails
    const claims = resolvedAccount?.idTokenClaims;
    if (claims) {
      return getEmail(claims);
    }

    return null;
  }, [resolvedAccount]);

  useEffect(() => {
    const checkUserSubscription = async () => {
      const hasInstitutionSubscriptions = institutionSubscriptions.length > 0;
      const shouldCheckB2c = !b2cDisabled && Boolean(userEmail);
      const b2cCheckKey = shouldCheckB2c ? `${userEmail}::${resolvedTargetApp}` : null;

      if (shouldCheckB2c && lastB2cCheckKeyRef.current === b2cCheckKey) {
        return;
      }

      // Reset states for new check
      setSubscriptionCheckAttempted(false);

      if (shouldCheckB2c) {
        lastB2cCheckKeyRef.current = b2cCheckKey;
        setSubscriptionDetails(
          mergeSubscriptionDetails({ allSubscriptions: [] }, institutionAuthRef.current),
        );
        setSubscriptionLoading(true);
        try {
          const subscriptionResult = await checkSubscription(userEmail, resolvedTargetApp);
          setSubscriptionDetails(
            mergeSubscriptionDetails(
              {
                ...subscriptionResult,
                b2cCheckedEmail: userEmail,
                b2cCheckedTargetApp: resolvedTargetApp,
              },
              institutionAuthRef.current,
            ),
          );
        } catch (err) {
          console.warn("Error checking user subscription", err);
          setSubscriptionDetails(
            mergeSubscriptionDetails(
              {
                isValid: false,
                subscription: null,
                expiryDate: null,
                targetApp: resolvedTargetApp ?? "obesid",
                error: err.message || "Check failed",
                allSubscriptions: [],
                b2cCheckedEmail: userEmail,
                b2cCheckedTargetApp: resolvedTargetApp,
              },
              institutionAuthRef.current,
            ),
          );
        } finally {
          setSubscriptionLoading(false);
          setSubscriptionCheckAttempted(true);
        }
        return;
      }

      lastB2cCheckKeyRef.current = null;
      // Mark that we've attempted the check (even if skipped)
      setSubscriptionCheckAttempted(true);

      // If institution auth exists (or B2C is disabled), still populate merged details
      if (hasInstitutionSubscriptions || institutionAuth || b2cDisabled) {
        setSubscriptionDetails(
          mergeSubscriptionDetails(
            {
              isValid: hasInstitutionSubscriptions || b2cDisabled,
              subscription: institutionAuth || null,
              expiryDate: null,
              targetApp: resolvedTargetApp ?? "obesid",
              reason: institutionAuth ? "institution_auth" : "b2c_disabled",
              allSubscriptions: [],
              b2cCheckedEmail: null,
              b2cCheckedTargetApp: null,
            },
            institutionAuthRef.current,
          ),
        );
        return;
      }

      // If there's no email and B2C is enabled, surface a login prompt
      if (!userEmail && !b2cDisabled) {
        setSubscriptionDetails(
          mergeSubscriptionDetails(
            {
              isValid: false,
              subscription: null,
              expiryDate: null,
              targetApp: resolvedTargetApp ?? "obesid",
              error: "Log in to continue",
              allSubscriptions: [],
              b2cCheckedEmail: null,
              b2cCheckedTargetApp: null,
            },
            institutionAuthRef.current,
          ),
        );
      }
    };

    checkUserSubscription();
  }, [
    userEmail,
    b2cDisabled,
    institutionAuth,
    institutionSubscriptions,
    mergeSubscriptionDetails,
    resolvedTargetApp,
  ]);

  const refreshUserProfile = useCallback(async () => {
    if (!userEmail) {
      setUserProfile(null);
      setUserProfileError(null);
      setUserProfileLoading(false);
      return null;
    }

    setUserProfileLoading(true);
    setUserProfileError(null);

    try {
      // const response = isSalesforceEnabled()
      //   ? await apiService.getUserProfile(userEmail)
      // :
      const response = await apiService.getUserProfile(userEmail, true, {
        forceRefresh: true,
      });

      if (!response.success) {
        throw new Error(response.message || "Failed to fetch profile data");
      }

      setUserProfile(response.data);
      return response.data;
    } catch (error) {
      setUserProfile(null);
      setUserProfileError(error.message || "An error occurred while fetching profile data");
      throw error;
    } finally {
      setUserProfileLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    let isMounted = true;

    if (!userEmail) {
      setUserProfile(null);
      setUserProfileError(null);
      setUserProfileLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setUserProfileLoading(true);
    setUserProfileError(null);

    apiService
      .getUserProfile(userEmail, true)
      .then((response) => {
        if (!isMounted) return;

        if (!response.success) {
          throw new Error(response.message || "Failed to fetch profile data");
        }

        setUserProfile(response.data);
      })
      .catch((error) => {
        if (!isMounted) return;
        setUserProfile(null);
        setUserProfileError(error.message || "An error occurred while fetching profile data");
      })
      .finally(() => {
        if (isMounted) {
          setUserProfileLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [userEmail]);

  // Keep institution subscriptions in sync without wiping B2C details
  useEffect(() => {
    if (!institutionAuth) return;
    setSubscriptionDetails((prev) =>
      mergeSubscriptionDetails(prev || { allSubscriptions: [] }, institutionAuth),
    );
  }, [institutionAuth, mergeSubscriptionDetails]);

  // MSAL loading state
  useEffect(() => {
    if (b2cDisabled || resolvedAccount) {
      setMsalLoading(false);
    } else {
      const timer = setTimeout(() => setMsalLoading(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [resolvedAccount, b2cDisabled]);

  useEffect(() => {
    if (silentLoginAttempted) return;
    if (b2cDisabled) {
      setSilentLoginAttempted(true);
      return;
    }

    // Already logged in
    if (accounts.length > 0) {
      // const postLoginAction = sessionStorage.getItem("postLoginAction");
      // if (postLoginAction === "subscribe") {
      //   sessionStorage.removeItem("postLoginAction");
      //   window.location.replace(
      //     "https://00dou00000cqr1lmad-d6ad3202.storeconnect.app/subscriptions",
      //   );
      // }
      setSilentLoginAttempted(true);
      return;
    }

    // MSAL still doing something
    if (inProgress !== "none") {
      return;
    }

    // Attempt silent SSO
    instance
      .ssoSilent({
        scopes: ["openid", "profile"],
      })
      .then((response) => {
        if (response?.account) {
          setSilentAuthAccount(response.account);
          if (typeof instance.setActiveAccount === "function") {
            instance.setActiveAccount(response.account);
          }
        }
        console.log("Silent SSO success");
      })
      .catch((err) => {
        if (err.errorCode === "interaction_required") {
          console.log("No SSO session — user must sign in manually");
        } else {
          console.warn("Silent SSO failed", err);
        }
      })
      .finally(() => {
        setSilentLoginAttempted(true);
      });
  }, [instance, accounts.length, inProgress, b2cDisabled]);

  useEffect(() => {
    if (accounts.length === 0 && isLoggingOut) {
      setSilentAuthAccount(null);
      setSilentLoginAttempted(false);
    }
  }, [accounts.length, isLoggingOut]);

  useEffect(() => {
    if (accounts.length === 0 && !b2cDisabled && !silentAuthAccount) {
      setSilentLoginAttempted(false);
    }
  }, [accounts.length, b2cDisabled, silentAuthAccount]);

  // Sync authID with MSAL
  useEffect(() => {
    if (!resolvedAccount || !resolvedAccount.idTokenClaims) {
      b2cDisabled
        ? authID?.u !== " " && dispatch(updateUser(" "))
        : authID?.u !== null && dispatch(updateUser(null));
      setMsalLoading(false);
      return;
    }
    if (resolvedAccount && !authID?.u) {
      // Only if authID is empty
      const { idTokenClaims } = resolvedAccount;
      const email = idTokenClaims["signInNames.emailAddress"] || idTokenClaims.email;
      const tgoaEmail = `member@${email?.split("@")[1] || ""}`;
      const institution = tgoa[tgoaEmail] || null;
      dispatch(
        updateUser({
          ...idTokenClaims,
          institution,
          shortName: "single-multi",
        }),
      );
    }
    setMsalLoading(false);
  }, [resolvedAccount, authID?.u, b2cDisabled, dispatch]);

  useEffect(() => {
    if (authID?.u) {
      if (authID?.u?.["signInNames.emailAddress"]) {
        dispatch(fetchSubscriptionStatusThunk(authID?.u?.["signInNames.emailAddress"]));
      }
    }
  }, [authID?.u]);

  useEffect(() => {
    if (!hasFetched) {
      dispatch(fetchB2cDisabled({ b2cDisabled, authID }));
      setHasFetched(true);
    }
  }, [b2cDisabled, dispatch, hasFetched]);

  // Helper function for checking zip file
  const checkZip = () => {
    let checkZipFile = !zipFile && zipFile !== 1 ? 1 : zipFile;
    if ((!authID.u || authID.u === " ") && institution.length !== undefined) {
      checkZipFile = null;
    }

    if (authID.u === " " && authID.i === " " && iDB) {
      iDB
        .keys()
        .then(async (keys) => {
          if (keys.length > IDB_KEY_LENGTH) {
            console.log(`Cancelling download, found: ${keys.length} files in iDB.`);
            const worker = getDownloadWorker();
            if (worker) {
              worker.postMessage({ command: "Hydrated" });
            }
            await logoutCleanUp(iDB);
          } else {
            console.log("downloading...keys=", keys.length);
            checkZipFile = !zipFile && zipFile !== 1 ? 1 : zipFile;
            if (checkZipFile === 1) {
              console.log("Guest", checkZipFile);
              dispatch(updateState({ zip: checkZipFile }));
            }
          }
        })
        .catch((error) => {
          console.error("useAuth - checkZip iDB.keys() error:", error);
        });
    }
    return checkZipFile;
  };
  //register onMessage of downloadworker to receive progress updates it can be on start of app
  // This took out so that multiple components can use useAuth without multiple onmessage registrations
  useEffect(() => {
    const downloadWorker = getDownloadWorker();
    if (!downloadWorker) return;
    downloadWorker.onmessage = function (event) {
      if (event.data.type === "clear_download_tracking") {
        // Clear progress tracking data only - NEVER deletes actual .zip files from Cache API
        // Zip files remain cached permanently for offline access
        try {
          sessionStorage.removeItem(event.data.key);
          localStorage.removeItem(event.data.key);
        } catch (err) {
          console.warn("Could not clear tracking:", err);
        }
        return;
      }

      // Handle zip metadata (can't transfer JSZip objects directly)
      if (event.data.type === "zip_metadata") {
        const { zipType, fileCount, ccmsversion: version } = event.data;
        console.log(`Received ${zipType} metadata: ${fileCount} files`);

        // Defer heavy operations to prevent blocking UI thread
        Promise.resolve()
          .then(async () => {
            const cacheName = import.meta.env.REACT_APP_CACHE_NAME;
            const underscoreType = zipType === "content" ? "" : `_${zipType}`;
            const url = `/viewTopic/${version}${underscoreType}.zip?__WB_REVISION__=${import.meta.env.REACT_APP_CONTENT_VERSION}`;

            const cache = await caches.open(cacheName);
            const response = await cache.match(url);

            if (!response) {
              console.warn(`No cached response found for ${zipType}`);
              return;
            }

            const buffer = await response.arrayBuffer();
            if (!buffer) return;

            // Yield to browser between operations to prevent UI blocking
            await new Promise((resolve) => setTimeout(resolve, 0));

            // Decrypt in chunks to avoid blocking
            const encoder = new TextEncoder();
            const keyBytes = encoder.encode(import.meta.env.REACT_APP_OFFLINE_SECRET);
            const cryptoKey = await crypto.subtle.importKey(
              "raw",
              keyBytes,
              { name: "AES-CTR", length: 256 },
              false,
              ["decrypt"],
            );

            await new Promise((resolve) => setTimeout(resolve, 0));

            const iv = new Uint8Array(buffer.slice(0, 16));
            const ciphertext = new Uint8Array(buffer.slice(16));
            const decryptedBuffer = await crypto.subtle.decrypt(
              { name: "AES-CTR", counter: iv, length: 64 },
              cryptoKey,
              ciphertext,
            );

            // Yield before heavy JSZip parsing
            await new Promise((resolve) => setTimeout(resolve, 0));

            const zip = await JSZip.loadAsync(decryptedBuffer);
            console.log(`📂 ${zipType} zip loaded with ${Object.keys(zip.files).length} files`);

            // Update state with this zip part
            const parts = {
              ...downloadZip,
              [zipType]: zip,
            };

            // Check if all three arrived
            if (!parts.content || !parts.images || !parts.PDFs) {
              dispatch(updateState({ downloadZip: parts }));
              console.log(
                `⏳ Waiting for remaining zip parts... (have: ${Object.keys(parts)
                  .filter((k) => parts[k])
                  .join(", ")})`,
              );
              return;
            }

            // All three arrived - merge them (yield first to keep UI responsive)
            await new Promise((resolve) => setTimeout(resolve, 0));
            console.log("🧩 All zip parts ready. Merging...");
            const mergedZip = new JSZip();

            // Merge in batches to avoid blocking
            let fileCount = 0;
            for (const partZip of Object.values(parts)) {
              for (const file of Object.values(partZip.files)) {
                if (!file.dir) {
                  mergedZip.file(file.name, file.async("arraybuffer"));
                  fileCount++;

                  // Yield every 100 files to keep UI responsive
                  if (fileCount % 100 === 0) {
                    await new Promise((resolve) => setTimeout(resolve, 0));
                  }
                }
              }
            }

            await new Promise((resolve) => setTimeout(resolve, 0));

            dispatch(
              updateState({
                zip: mergedZip,
                downloadZip: {
                  content: null,
                  images: null,
                  PDFs: null,
                },
              }),
            );
            console.log("✅ Zip merge complete");

            // 🧹 MEMORY CLEANUP: Clear individual zip parts (they're merged now)
            Object.keys(parts).forEach((key) => {
              if (parts[key]) {
                parts[key] = null;
              }
            });
            console.log("🧹 Cleared individual zip parts from main thread memory");
          })
          .catch((err) => {
            console.error(`Error loading ${zipType} zip:`, err);
          });

        return;
      }

      if (event.data.type === "progress") {
        dispatch(updateDownloadProgress(event.data.progress));
        dispatch(
          updateDownloadIDBProgress({
            percent: event.data.progress,
            key: event.data.zipType,
          }),
        );

        // Persist IDB progress for resume
        const { progress, zipType, currentFileCount, totalFiles } = event.data;
        try {
          const progressKey = `idb_progress_${zipType}_${ccmsversion?.selected}`;
          localStorage.setItem(
            progressKey,
            JSON.stringify({
              progress,
              currentFileCount,
              totalFiles,
              timestamp: Date.now(),
            }),
          );
          if (progress >= 100) {
            // Clear IDB progress tracking only - .zip files remain in Cache API
            localStorage.removeItem(progressKey);

            // Check if all IDB processing is complete
            const state = window.store?.getState();
            if (state) {
              const idbProgress = state.counter.downloadIDBProgress;
              const allComplete =
                idbProgress.contentsReceived === 100 &&
                idbProgress.imagesReceived === 100 &&
                idbProgress.pdfsReceived === 100;

              if (allComplete) {
                console.log("✅ All IDB processing complete");
                workersStartedGlobal = false; // Allow restart on next version change
              }
            }
          }
        } catch (err) {
          console.warn("Could not persist IDB progress:", err);
        }
      } else if (event.data.type === "images_progress_zip") {
        dispatch(
          updateDownloadZipProgress({
            key: "imagesReceived",
            percent: event.data.percent,
          }),
        );
        // Persist progress (don't clear at 100% - needed for merge coordination)
        if (event.data.downloadKey) {
          try {
            localStorage.setItem(
              event.data.downloadKey,
              JSON.stringify({
                percent: event.data.percent,
                received: event.data.received,
                total: event.data.total,
                timestamp: Date.now(),
              }),
            );
          } catch (err) {}
        }
      } else if (event.data.type === "PDFs_progress_zip") {
        dispatch(
          updateDownloadZipProgress({
            key: "pdfsReceived",
            percent: event.data.percent,
          }),
        );
        // Persist progress (don't clear at 100% - needed for merge coordination)
        if (event.data.downloadKey) {
          try {
            localStorage.setItem(
              event.data.downloadKey,
              JSON.stringify({
                percent: event.data.percent,
                received: event.data.received,
                total: event.data.total,
                timestamp: Date.now(),
              }),
            );
          } catch (err) {}
        }
      } else if (event.data.type === "progress_zip") {
        dispatch(
          updateDownloadZipProgress({
            key: "contentsReceived",
            percent: event.data.percent,
          }),
        );
        // Persist progress (don't clear at 100% - needed for merge coordination)
        if (event.data.downloadKey) {
          try {
            localStorage.setItem(
              event.data.downloadKey,
              JSON.stringify({
                percent: event.data.percent,
                received: event.data.received,
                total: event.data.total,
                timestamp: Date.now(),
              }),
            );
          } catch (err) {}
        }
      }
    };
    return () => {
      downloadWorker.onmessage = null;
    };
  }, []);
  // checking Institution user
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];

    //use updated referral flow.
    const referrer = getReferrer();

    function processGetReqInfoData(data) {
      if (data?.sub?.length > 0) {
        const ins = {
          name:
            data.account?.name ||
            data.sub[0]["_tg_customer_value@OData.Community.Display.V1.FormattedValue"],
          id: data.sub[0]["_tg_customer_value"], // its used in getInstitution for fetching account
          orgid: data.sub[0]["_organizationid_value"],
          shortName: data.account.tg_shortname || "cannot find institution short name",
          instSubscriptionId:
            data.entitlement.length > 0 ? data.entitlement[0].entitlementid : null, // for salesforce
        };

        let shouldLog = true;
        if (sessionStorage.getItem("InstUserLastLoginEvent")) {
          const timePassed =
            Date.now() - parseInt(sessionStorage.getItem("InstUserLastLoginEvent"), 10);
          if (timePassed < 21600000) {
            shouldLog = false;
          } else {
            sessionStorage.removeItem("InstUserLastLoginEvent");
          }
        }
        if (shouldLog) {
          window.dataLayer.push({
            event: "login",
            ip: data.ip,
            institution: ins.name,
            origin: referrer ? "referrer" : "IP",
          });
          sessionStorage.setItem("InstUserLastLoginEvent", Date.now());
        }

        let entitlements = data.entitlement;
        let expired = true;
        let expiredDate = new Date();
        if (entitlements !== undefined && entitlements.length > 0) {
          for (let entitlement of entitlements) {
            if (entitlement.tg_subscriptionid === "170") {
              const startDate = new Date(entitlement.startdate);
              expiredDate = new Date(entitlement.tg_expirydate);
              const today = new Date();
              expiredDate.setHours(0, 0, 0, 0);
              today.setHours(0, 0, 0, 0);
              expired = today < startDate || today > expiredDate;
              break;
            }
          }
        }

        dispatch(setExpireSubscription(expired));

        let ip_access_token = btoa(
          JSON.stringify({
            ...ins,
            subscriptions: entitlements ?? [],
          }),
        );
        localStorage.setItem("tgt", ip_access_token);
        localStorage.setItem("tg_expirydate", btoa(JSON.stringify(expiredDate)));
        setInstitution(ins);

        if (ins?.length === undefined) {
          dispatch(updateInstitution(ins));
          dispatch(updateState({ zip: checkZip() }));
        }
      } else {
        // console.log("useAuth - No institution data found for INS.");
        dispatch(setInstitutionLoading(false));
      }
      dispatch(updateState({ ip: data.ip }));
    }

    if (getReqInfoCalled) {
      if (getReqInfoCache) {
        processGetReqInfoData(getReqInfoCache);
      } else if (getReqInfoPromise) {
        getReqInfoPromise
          .then((data) => {
            processGetReqInfoData(data);
          })
          .catch((error) => {
            dispatch(setInstitutionLoading(false));
          });
      }
      return;
    }
    let expiredDate = null;
    try {
      let decodednstitution = JSON.parse(atob(localStorage.getItem("tgt")));
      expiredDate = new Date(JSON.parse(atob(localStorage.getItem("tg_expirydate"))));

      if (decodednstitution && expiredDate > new Date()) {
        setInstitution(decodednstitution);
        if (
          typeof decodednstitution === "object" &&
          decodednstitution !== null &&
          !Array.isArray(decodednstitution)
        ) {
          dispatch(updateInstitution(decodednstitution));
          dispatch(updateState({ zip: checkZip() }));
        }
      }
    } catch (error) {
      console.log("useAuth - No valid token found." + error);
    }
    // Dynamically select API endpoint based on provider
    const endpoint = isSalesforceEnabled() ? "SalesforceGetReqInfo" : "getReqInfo";
    // console.log(`🔵 useAuth - Using ${apiProvider} API, calling /api/${endpoint}`);
    // if(referrer==='https://localhost:3000/' ||referrer === 'https://sftgtest.b2clogin.com/' || referrer === 'https://sftgprod.b2clogin.com/' || referrer === 'https://login.test.tg.org.au/' || referrer === 'https://login.tg.org.au/'){
    //   return;
    // }
    // To avoid too much getReqInfoapi calls
    if (expiredDate >= new Date()) {
      dispatch(setInstitutionLoading(false));
      //   console.log("useAuth - Subscription expired based on local storage date. and its  not expired yet");
      return;
    }

    let url;
    if (!referrer) {
      console.log("useAuth - No valid referrer found.");
      url = `/api/${endpoint}`;
    } else {
      url = `/api/${endpoint}?r=` + referrer;
    }

    if (!getReqInfoPromise) {
      getReqInfoPromise = fetch(url)
        .then((response) => response.json())
        .then((data) => {
          getReqInfoCache = data;
          getReqInfoCalled = true;
          return data;
        })
        .catch((error) => {
          console.error("useAuth - Failed to fetch institution info:", error);
          getReqInfoPromise = null;
          throw error;
        });
    }
    getReqInfoPromise
      .then((data) => {
        processGetReqInfoData(data);
      })
      .catch((error) => {
        dispatch(setInstitutionLoading(false));
      });
  }, []);

  const isLogged = useMemo(() => {
    return !!userLogged(authID);
  }, [authID]);

  // const isLoading = msalLoading || institutionLoading;

  // Track MSAL user login events
  useEffect(() => {
    if (msalUserLoginEventSent) {
      return;
    }

    const details = authDetails(authID);

    if (!isLogged || !details || details.type === TYPE_INSTITUTION) {
      return;
    }

    let shouldLog = true;
    if (sessionStorage.getItem("SingleUserLastLoginEvent")) {
      const timePassed =
        Date.now() - parseInt(sessionStorage.getItem("SingleUserLastLoginEvent"), 10);
      if (timePassed < 21600000) {
        // 21600000 is 6 hours in milliseconds
        shouldLog = false;
      } else {
        sessionStorage.removeItem("SingleUserLastLoginEvent");
      }
    }

    if (!shouldLog) {
      return;
    }
    msalUserLoginEventSent = true; // set at here to aviod multiple calls

    // Use cached data from the institution check if available
    if (getReqInfoCache) {
      logMsalUserLoginEvent(getReqInfoCache, details);
    } else {
      // Dynamically select API endpoint based on provider
      const endpoint = isSalesforceEnabled() ? "SalesforceGetReqInfo" : "getReqInfo";

      fetch(`/api/${endpoint}`)
        .then((response) => response.json())
        .then((data) => {
          logMsalUserLoginEvent(data, details);
        })
        .catch((error) => {
          console.error("useAuth - Failed to fetch IP for user login event:", error);
        });
    }

    function logMsalUserLoginEvent(data, authDetails) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "login",
        given_name: authDetails?.givenName ?? "",
        family_name: authDetails?.familyName ?? "",
        email: authDetails?.email ?? "",
        ip: data.ip,
        institution: authDetails?.institution ?? "single user who doesn't have institution name",
        origin: "MSAL",
      });
      sessionStorage.setItem("SingleUserLastLoginEvent", Date.now());
    }
  }, [authID, isLogged]);

  // Check for cached downloads on mount and restore progress indicators
  useEffect(() => {
    const checkCachedDownloads = async () => {
      if (!ccmsversion?.selected) return;

      try {
        const cache = await caches.open(import.meta.env.REACT_APP_CACHE_NAME);
        const types = [
          { type: "", key: "contentsReceived" },
          { type: "_images", key: "imagesReceived" },
          { type: "_PDFs", key: "pdfsReceived" },
        ];

        for (const { type, key } of types) {
          const url = `/viewTopic/${ccmsversion.selected}${type}.zip?__WB_REVISION__=${import.meta.env.REACT_APP_CONTENT_VERSION}`;
          const cached = await cache.match(url);

          const downloadKey = `download_${type ? type.substring(1) : "content"}_${ccmsversion.selected}`;
          const savedProgress = localStorage.getItem(downloadKey);

          if (cached) {
            console.log(`Found cached ${type || "content"} zip`);
            // Set progress to 100% for cached files
            dispatch(updateDownloadZipProgress({ key, percent: 100 }));

            // Clear progress tracking ONLY - actual .zip file stays in Cache API permanently
            localStorage.removeItem(downloadKey);
          } else if (savedProgress) {
            // File not cached but has progress - restore the progress state
            try {
              const progress = JSON.parse(savedProgress);
              const age = Date.now() - progress.timestamp;

              // If recent (< 2 min), restore progress to UI (might still be downloading in worker)
              if (age < 120000 && progress.percent < 100) {
                console.log(
                  `Restoring in-progress download for ${type || "content"}: ${progress.percent}%`,
                );
                dispatch(updateDownloadZipProgress({ key, percent: progress.percent }));
              } else if (progress.percent === 100 && age < 120000) {
                // Progress at 100% but not cached - worker might still be caching
                console.log(
                  `Download complete (${progress.percent}%), waiting for cache confirmation: ${type || "content"}`,
                );
                dispatch(updateDownloadZipProgress({ key, percent: progress.percent }));
              }
              // Don't clear localStorage here - let worker's clear_download_tracking handle it
            } catch (err) {
              console.warn("Could not parse saved progress:", err);
            }
          }
        }
      } catch (error) {
        console.error("Error checking cached downloads:", error);
      }
    };

    checkCachedDownloads();
  }, [ccmsversion, dispatch]);

  useEffect(() => {
    // Skip downloads during automated testing
    if (isAutomateTest()) {
      console.log("useAuth - Skipping download workers for automated test");
      return;
    }

    if (!((zipFile === 1 || (zipFile === null && isLogged)) && expired === false)) {
      console.log(
        "useAuth - Not starting workers, conditions not met. {zipFile, isLogged, expired}",
        zipFile,
        isLogged,
        expired,
      );
      return;
    }

    if (!(window.Worker && iDB)) {
      console.log("Web Workers are not supported in this browser.");
      return;
    }

    // ========================================================================
    // SYNCHRONOUS DUPLICATE CHECK - Critical for preventing race conditions
    // ========================================================================
    // MUST check and set flag BEFORE any async operations (iDB.keys())
    // When multiple components mount simultaneously:
    // - First component: Sets workersStartedGlobal=true synchronously
    // - Other components: See flag=true and return immediately
    // This prevents all components from entering the async chain and starting workers
    if (workersStartedGlobal) {
      console.log("Workers already started (pre-check), skipping duplicate start");
      return;
    }

    // Set flag immediately to block other components from starting workers
    // This happens BEFORE iDB.keys() async call starts
    workersStartedGlobal = true;
    console.log("🔒 Set workersStartedGlobal=true (pre-async lock)");

    iDB
      .keys()
      .then(async (keys) => {
        // ========================================================================
        // WORKER STARTUP LOGIC - IDB check and worker initialization
        // ========================================================================

        // 1. Check if IDB already complete (has all files)
        if (keys.length > IDB_KEY_LENGTH) {
          console.log("IDB already has all files, skipping download");
          workersStartedGlobal = false; // Reset flag - allows restart on next version change
          return; // Already have all files in IDB, nothing to do
        }

        // 2. Check cache status BEFORE starting workers to prevent restart appearance
        const { allCached, cachedFiles, uncachedFiles } = await checkZipCacheStatus(
          ccmsversion?.selected,
        );

        // 3. Set progress to 100% for cached files IMMEDIATELY (prevents UI flicker)
        //    This runs before workers start, so users don't see downloads "restarting"
        const progressMap = {
          content: "contentsReceived",
          images: "imagesReceived",
          PDFs: "pdfsReceived",
        };

        cachedFiles.forEach((cachedFile) => {
          const progressKey = progressMap[cachedFile];
          if (progressKey) {
            dispatch(updateDownloadZipProgress({ key: progressKey, percent: 100 }));
            console.log(`✅ ${cachedFile} already cached, set progress to 100%`);
          }
        });

        // 4. If all zips cached and IDB nearly complete, skip worker startup
        //    Workers would only extract from cache, but if IDB is close to done,
        //    we can skip to avoid appearance of "restarting" downloads
        const idbCompletionPercent = (keys.length / IDB_KEY_LENGTH) * 100;
        if (allCached && idbCompletionPercent > 95) {
          console.log(
            `✅ All zips cached and IDB ${idbCompletionPercent.toFixed(1)}% complete, skipping worker restart`,
          );
          workersStartedGlobal = false; // Allow next version to trigger properly
          return;
        }

        if (allCached) {
          console.log("✅ All zips cached, workers will extract from cache only (no download)");
        } else if (cachedFiles.length > 0) {
          console.log(
            `⚠️ Partially cached: ${cachedFiles.join(", ")} cached | ${uncachedFiles.join(", ")} need download`,
          );
        } else {
          console.log("⚠️ No zips cached, workers will download all files");
        }

        // 5. Start workers - flag already set synchronously above
        console.log("useAuth - Starting download workers. IDB has", keys.length, "files");

        const downloadWorker = getDownloadWorker();
        if (!downloadWorker) {
          workersStartedGlobal = false; // Reset flag if worker creation fails
          return;
        }

        // ========================================================================
        // WORKER EXECUTION - Cache-first approach for resume capability
        // ========================================================================
        // Workers ALWAYS start (even if zips cached) to complete IDB extraction:
        // - Cache-first check in download.js (line 38-63): If cached → use cache, else download
        // - If all cached: Process from cache → Extract to IDB (resume from partial)
        // - If not cached: Download → Cache → Extract to IDB
        // - Page refresh: workersStartedGlobal resets → workers restart → resume IDB extraction
        //
        // DUPLICATE PREVENTION (KEY INSIGHT: Synchronous flag before async):
        // ✓ workersStartedGlobal=true set BEFORE iDB.keys() async call (line 778)
        // ✓ Prevents race when multiple components mount simultaneously
        // ✓ First component: Sets flag synchronously → others see it and skip
        // ✓ Flag persists across component remounts (same page, same module instance)
        // ✓ Flag resets on: page refresh (module reload) OR IDB complete (line 399)
        const startWorkers = async () => {
          const version = ccmsversion?.selected;

          console.log("📥 Starting download workers (cache-first strategy)");

          // Each worker checks cache first, then downloads only if needed
          downloadWorker.postMessage({
            command: "fetchAndStore",
            idbKeys: keys,
            ccmsversion: version,
          });

          downloadWorker.postMessage({
            command: "cacheLargeImagesZip",
            idbKeys: keys,
            type: "images",
            ccmsversion: version,
          });

          downloadWorker.postMessage({
            command: "cacheLargePdfsZip",
            type: "PDFs",
            idbKeys: keys,
            ccmsversion: version,
          });
        };

        startWorkers().catch((error) => {
          console.error("Error starting workers:", error);
        });
      })
      .catch((error) => {
        console.error("useAuth - download worker iDB.keys() error:", error);
      });
  }, [zipFile, isLogged, expired, iDB, ccmsversion, dispatch]);
  //  Explicitly track if the check is still pending
  const isSubscriptionPending = useMemo(() => {
    const hasInstitutionCurrentAppAccess = Boolean(
      validateSubscription(institutionSubscriptions, resolvedTargetApp),
    );
    const needsB2cFallbackForCurrentApp =
      !b2cDisabled && Boolean(userEmail) && !hasInstitutionCurrentAppAccess;
    return (
      needsB2cFallbackForCurrentApp &&
      (subscriptionLoading ||
        !subscriptionCheckAttempted ||
        subscriptionDetails?.b2cCheckedEmail !== userEmail ||
        subscriptionDetails?.b2cCheckedTargetApp !== resolvedTargetApp)
    );
  }, [
    b2cDisabled,
    institutionSubscriptions,
    resolvedTargetApp,
    subscriptionLoading,
    userEmail,
    subscriptionCheckAttempted,
    subscriptionDetails,
  ]);
  const isLoading = useMemo(() => {
    if (isLoggingOut) return true;

    const hasCurrentAppAccessFromKnownSource = Boolean(
      validateSubscription(institutionSubscriptions, currentApp) ||
      validateSubscription(subscriptionDetails?.b2cSubscriptions || [], currentApp),
    );

    if (hasCurrentAppAccessFromKnownSource) {
      return false;
    }

    return Boolean(
      msalLoading ||
      isSubscriptionPending ||
      !institutionAuthChecked ||
      (subscriptionLoading && !hasCurrentAppAccessFromKnownSource) ||
      (!silentLoginAttempted && !b2cDisabled),
    );
  }, [
    b2cDisabled,
    currentApp,
    institutionSubscriptions,
    msalLoading,
    subscriptionLoading,
    isSubscriptionPending,
    institutionAuthChecked,
    isLoggingOut,
    silentLoginAttempted,
    subscriptionDetails,
  ]);

  const allSubscriptions = useMemo(() => {
    // Prefer merged subscriptions when available, otherwise fall back to institution-only.
    if (subscriptionDetails?.allSubscriptions?.length > 0) {
      return subscriptionDetails.allSubscriptions;
    }
    return institutionAuth?.subscriptions || [];
  }, [institutionAuth, subscriptionDetails]);
  const activeSubscriptions = useMemo(() => {
    return allSubscriptions.filter((sub) => isSubscriptionActiveNow(sub));
  }, [allSubscriptions]);

  const availableAppSlugs = useMemo(() => {
    const subscribed = Object.keys(APP_ID_GROUPS).filter(
      (slug) => !!validateSubscriptionForAccess(allSubscriptions, slug),
    );
    // Only surface apps whose whitelabel flag is enabled
    return filterByAvailability(subscribed);
  }, [allSubscriptions, filterByAvailability]);
  const isSubscribed = useMemo(() => {
    return !!validateSubscription(allSubscriptions, currentApp);
  }, [allSubscriptions, currentApp]);
  const currentAppSubscription = useMemo(() => {
    return validateSubscription(allSubscriptions, currentApp) || null;
  }, [allSubscriptions, currentApp]);
  const institutionCurrentAppSubscription = useMemo(() => {
    return validateSubscription(institutionSubscriptions, currentApp) || null;
  }, [institutionSubscriptions, currentApp]);
  const b2cCurrentAppSubscription = useMemo(() => {
    return validateSubscription(subscriptionDetails?.b2cSubscriptions || [], currentApp) || null;
  }, [subscriptionDetails, currentApp]);
  const currentAppAuthType = useMemo(() => {
    if (institutionCurrentAppSubscription) {
      return TYPE_INSTITUTION;
    }

    if (userEmail || b2cCurrentAppSubscription) {
      return TYPE_USER;
    }

    if (institutionAuth) {
      return TYPE_INSTITUTION;
    }

    return buildAuthDetails(
      resolvedAccount?.idTokenClaims,
      authID,
      institutionAuth,
      subscriptionDetails?.subscription,
    )?.type;
  }, [
    institutionCurrentAppSubscription,
    userEmail,
    b2cCurrentAppSubscription,
    institutionAuth,
    buildAuthDetails,
    resolvedAccount?.idTokenClaims,
    authID,
    subscriptionDetails?.subscription,
  ]);

  const resolvedAuthDetails = useMemo(() => {
    if (currentAppAuthType === TYPE_USER) {
      if (resolvedAccount?.idTokenClaims) {
        return authDetailsFromClaims(resolvedAccount.idTokenClaims);
      }
      if (authID?.u && authID?.u !== " ") {
        return authDetails(authID);
      }
      return userEmail ? { type: TYPE_USER, email: userEmail } : null;
    }

    if (currentAppAuthType === TYPE_INSTITUTION) {
      const authIdDetails = authDetails(authID);
      const name =
        institutionAuth?.institutionName ||
        institutionAuth?.institution ||
        institutionAuth?.name ||
        institutionCurrentAppSubscription?.institutionName ||
        institutionCurrentAppSubscription?.institution ||
        authIdDetails?.institutionName ||
        authIdDetails?.institution ||
        null;

      return {
        type: TYPE_INSTITUTION,
        institution: name,
        institutionName: name,
      };
    }

    return buildAuthDetails(
      resolvedAccount?.idTokenClaims,
      authID,
      institutionAuth,
      subscriptionDetails?.subscription,
    );
  }, [
    currentAppAuthType,
    resolvedAccount?.idTokenClaims,
    authID,
    userEmail,
    institutionAuth,
    institutionCurrentAppSubscription,
    buildAuthDetails,
    subscriptionDetails?.subscription,
  ]);
  const hasAnySubscription = useMemo(() => {
    return availableAppSlugs.length > 0;
  }, [availableAppSlugs]);

  useEffect(() => {
    if (!isSubscribed) return;
    try {
      if (isSubscribed) {
        localStorage.setItem(
          CACHE_SUBSCRIBED_KEY,
          JSON.stringify({
            hasCurrentAppAccess: true,
            appSlugs: availableAppSlugs || [],
            expiresAt: currentAppSubscription?.tg_expirydate ?? null,
          }),
        );
      }
    } catch (error) {
      console.warn("useAuth - failed to persist isSubscribed:", error);
    }
  }, [
    isSubscribed, // Only leave isSubscribed in dependency to prevent multiple calls
  ]);

  const setLoggingOut = useCallback(() => {
    setIsLoggingOut(true);
  }, []);

  useEffect(() => {
    if (isLoggingOut) {
      flagContext.clearStoredContext();
    }
    if (msalLoading || institutionLoading) {
      return; // loading => don't do anything yet
    }
    const authType = resolvedAuthDetails?.type;
    if (!authType) {
      // reset flag context - user is not authenticated
      flagContext.clearStoredContext();
    } else {
      // Note flagsmith identifier priority is: UserEmail > institutionName
      // However Login currently has login scenarios: B2C only, Institution only, B2C + Institution.
      // In B2C + Institution scenario, we want to prioritise user email for targeting since it's more specific

      if (authType === TYPE_USER) {
        // this is the B2C only scenario
        flagContext.email(userEmail).institute(null).type(TYPE_USER).build();
      } else if (authType === TYPE_INSTITUTION) {
        if (userEmail) {
          // this is the B2C + Institution scenario
          flagContext
            .email(userEmail)
            .institute(resolvedAuthDetails?.institutionName)
            .type(TYPE_USER)
            .build();
        } else {
          // this is the Institution only scenario
          flagContext
            .email(null)
            .institute(resolvedAuthDetails?.institutionName)
            .type(TYPE_INSTITUTION)
            .build();
        }
      }
    }
  }, [
    resolvedAuthDetails?.institutionName,
    resolvedAuthDetails?.type,
    msalLoading,
    institutionLoading,
    isLoggingOut,
    userEmail,
  ]);

  return {
    isLogged,
    isLoading,
    b2cDisabled,
    silentLoginAttempted,
    subscriptionLoading,
    subscriptionCheckAttempted,
    authID,
    subscriptionDetails,
    isSubscribed,
    institutionAuth,
    isLoggingOut,
    setLoggingOut,
    email: userEmail,
    availableAppSlugs,
    activeSubscriptions,
    currentAppSubscription,
    hasAnySubscription,
    userProfile,
    userProfileLoading,
    userProfileError,
    refreshUserProfile,
    institutionLoading,
    isInstitution: currentAppAuthType === TYPE_INSTITUTION,
    authDetails: resolvedAuthDetails,
  };
};

export const AuthProvider = ({ children }) => {
  const value = useAuthValue();

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export default useAuth;
